/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable default-case */
import { useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as PhoneIcon } from '../../../../assets/icons/call.svg';
import { ReactComponent as ContainerDeliveriesIcon } from '../../../../assets/icons/container-deliveries-fill.svg';
import { ReactComponent as EraserIcon } from '../../../../assets/icons/ereaser.svg';
import { ReactComponent as LocationIcon } from '../../../../assets/icons/location.svg';
import {
  CONTAINER_DELIVERIES_SERVICE_NAME,
  FAT_BONES_SERVICE_NAME,
  HOOD_CLEANING_SERVICE_NAME,
  serviceContainerDeliveriesOptions,
  serviceContainerDeliveriesPickUpOptions,
  serviceContainerDeliveriesType,
} from '../../../../config/constants';
import { useNotification } from '../../../../hooks/useNotification';
import { UPDATE_STOP } from '../../../../services/graphql/stops/stops-mutation';
import Dropdown from '../../common/Inputs/Dropdown';
import InputCardField from '../../common/Inputs/InputCardField';

type StopCardAddedProps = {
  businessName: string;
  phoneNumber: string;
  address: string;
  estGallons?: number;
  estBarrels?: number;
  estContainerSizePickUp?: number;
  estContainerSizeDropOff?: string;
  estSpecialRequirements?: string;
  id: string;
  clientId?: string | undefined;
  serviceId?: string | undefined;
  mutation: (arg: any) => void;
  existingStops: any[] | undefined;
  routeId: string;
  totalStopsEstimatedBarrels?: number;
  totalStopsEstimatedGallons?: number;
  service: string;
  refetchTruckCapacity?: () => void;
  isSecondaryService?: boolean;
  sType?: string;
};

const StopCardAdded: React.FC<StopCardAddedProps> = ({
  businessName,
  phoneNumber,
  address,
  estGallons,
  id,
  mutation,
  clientId,
  serviceId,
  existingStops,
  routeId,
  totalStopsEstimatedGallons,
  totalStopsEstimatedBarrels,
  service,
  refetchTruckCapacity,
  estBarrels,
  estContainerSizePickUp = 0,
  estContainerSizeDropOff = '',
  estSpecialRequirements = '',
  isSecondaryService,
  sType,
}) => {
  const { t } = useTranslation('common');

  const { notifySaveSuccess, notifySaveError } = useNotification();

  const [updateStop] = useMutation(UPDATE_STOP);
  const [stopEstGallons, setStopEstGallons] = useState(estGallons || 0);
  const [stopEstBarrels, setStopEstBarrels] = useState(estBarrels || 0);
  const [serviceType, setServiceType] = useState(sType);
  const [stopContainerSizePickUp, setStopContainerSizePickUp] =
    useState<number>(estContainerSizePickUp);
  const [stopContainerSizeDropOff, setStopContainerSizeDropOff] =
    useState<string>(estContainerSizeDropOff);
  const [stopSpecialRequirements, setStopSpecialRequirements] =
    useState<string>(estSpecialRequirements);
  const stopId = id;

  const handleSubmit = (
    field: string,
    value: number | string,
    existingValue: number | string,
  ) => {
    if (value !== existingValue) {
      updateStop({
        variables: {
          key: field,
          value: value.toString(),
          stopId,
          // TODO> tener en cuenta que enviamos gallons a pounds
          estimated_gallons: totalStopsEstimatedGallons || 0,
          estimated_barrel: totalStopsEstimatedBarrels || 0,
        },
        onCompleted: () => {
          notifySaveSuccess();
          if (refetchTruckCapacity) {
            refetchTruckCapacity();
          }
        },
        onError: () => {
          notifySaveError();
        },
      });
    }
  };

  const handleFieldChange = (field: string, value: number | string) => {
    switch (field) {
      case 'estimated_gallons':
        setStopEstGallons(Number(value));
        break;

      case 'estimated_barrel':
        setStopEstBarrels(Number(value));
        break;
      case 'container_size_pick_up':
        setStopContainerSizePickUp(Number(value));
        handleSubmit(
          'container_size_pick_up',
          Number(value),
          estContainerSizePickUp || 0,
        );
        break;
      case 'container_size_drop_off':
        setStopContainerSizeDropOff(value.toString());
        handleSubmit(
          'container_size_drop_off',
          value.toString(),
          estContainerSizeDropOff || '',
        );
        break;
      case 'special_requirements':
        setStopSpecialRequirements(value.toString());
        break;
      case 'service_type':
        setServiceType(value.toString());
        handleSubmit('service_type', value.toString(), '');
        break;
    }
  };

  const handleRemove = () => {
    const updatedStops = existingStops
      ? existingStops.filter(
          (stop) =>
            stop.client_id !== clientId || stop.service_id !== serviceId,
        )
      : [];

    mutation({
      variables: {
        routeId,
        stops: updatedStops,
      },
    });
  };

  useEffect(() => {
    setStopEstGallons(estGallons || 0);
  }, [estGallons]);

  useEffect(() => {
    setStopEstBarrels(estBarrels || 0);
  }, [estBarrels]);

  return (
    <div className="mb-10 flex w-full flex-col items-start justify-start gap-[15px] rounded-[10px] bg-white p-2.5 shadow">
      <div className="inline-flex items-center justify-between self-stretch">
        <div className="shrink grow basis-0 text-xs font-semibold text-zinc-800">
          {businessName}
        </div>
      </div>
      <div className="flex flex-col items-start justify-start gap-2.5 self-stretch">
        <div className="flex min-w-[80px] max-w-[120px] items-center justify-start gap-1 self-stretch">
          <PhoneIcon />
          <p className="truncate text-xs">{phoneNumber}</p>
        </div>

        <div className="flex min-w-[100px] max-w-[800px] items-center justify-start gap-1 self-stretch">
          <LocationIcon />
          <p className="truncate text-xs">{address}</p>
        </div>
      </div>
      {isSecondaryService && service === CONTAINER_DELIVERIES_SERVICE_NAME && (
        <div className="flex w-full items-center justify-center gap-x-2 rounded border border-reyesgold bg-goldextralight px-2 py-1 text-reyesgold">
          <ContainerDeliveriesIcon />
          <p>{t('routes.secondary-service-container-deliveries')}</p>
        </div>
      )}
      <div className="w-full">
        {service !== HOOD_CLEANING_SERVICE_NAME &&
          service !== FAT_BONES_SERVICE_NAME &&
          service !== CONTAINER_DELIVERIES_SERVICE_NAME && (
            <InputCardField
              type="number"
              label={t('routes.route-card-estimated-gallons')}
              unitGal={true}
              format="number"
              placeholder={stopEstGallons}
              selectedValue={stopEstGallons}
              onSelectChange={(value) =>
                handleFieldChange('estimated_gallons', Number(value))
              }
              onSave={() =>
                handleSubmit(
                  'estimated_gallons',
                  stopEstGallons || 0,
                  estGallons || 0,
                )
              }
              id="estimated-gallons-input-route-edit-field"
            />
          )}
        {service === FAT_BONES_SERVICE_NAME && (
          <InputCardField
            type="number"
            label={t('routes.route-card-estimated-barrel')}
            format="number"
            placeholder={stopEstBarrels}
            selectedValue={stopEstBarrels}
            onSelectChange={(value) =>
              handleFieldChange('estimated_barrel', Number(value))
            }
            onSave={() =>
              handleSubmit(
                'estimated_barrel',
                stopEstBarrels || 0,
                estBarrels || 0,
              )
            }
            id="estimated-gallons-input-route-edit-field"
          />
        )}
        {service === CONTAINER_DELIVERIES_SERVICE_NAME && (
          <>
            <div className="flex flex-col gap-y-4">
              <div className="w-full">
                <div className="flex items-center justify-between gap-2 border-b border-gray-400">
                  <div className="truncate text-sm font-normal text-gray-400">
                    {t('routes.route-card-containers-type')}
                  </div>
                  <Dropdown
                    selectClassname="!font-normal"
                    options={serviceContainerDeliveriesType}
                    selectedValue={serviceType || ''}
                    onSelectChange={(value) =>
                      handleFieldChange('service_type', value)
                    }
                  />
                </div>
              </div>
              {serviceType === 'Pick Up & Drop Off' && (
                <div className="w-full">
                  <div className="flex items-center justify-between gap-2 border-b border-gray-400">
                    <div className="truncate text-sm font-normal text-gray-400">
                      {t('routes.route-card-containers-to-pick-up')}
                    </div>
                    <Dropdown
                      selectClassname="!font-normal"
                      options={serviceContainerDeliveriesPickUpOptions}
                      selectedValue={stopContainerSizePickUp.toString() || ''}
                      onSelectChange={(value) =>
                        handleFieldChange(
                          'container_size_pick_up',
                          Number(value),
                        )
                      }
                    />
                  </div>
                </div>
              )}
              <div className="w-full">
                <div className="flex items-center justify-between gap-2 border-b border-gray-400">
                  <div className="truncate text-sm font-normal text-gray-400">
                    {t('routes.route-card-containers-to-drop-off')}
                  </div>
                  <Dropdown
                    selectClassname="!font-normal"
                    options={serviceContainerDeliveriesOptions}
                    selectedValue={stopContainerSizeDropOff || ''}
                    onSelectChange={(value) =>
                      handleFieldChange('container_size_drop_off', value)
                    }
                  />
                </div>
              </div>
              {stopContainerSizeDropOff === 'Special Requirements' && (
                <InputCardField
                  type="text"
                  label={t('routes.route-card-special-requirements')}
                  placeholder={stopSpecialRequirements}
                  selectedValue={stopSpecialRequirements}
                  onSelectChange={(value) =>
                    handleFieldChange('special_requirements', value)
                  }
                  onSave={() =>
                    handleSubmit(
                      'special_requirements',
                      stopSpecialRequirements || '',
                      estSpecialRequirements || '',
                    )
                  }
                  id="special-requirements-input-route-edit-field"
                />
              )}
            </div>
          </>
        )}
      </div>
      <div className="flex w-full items-center justify-end gap-[15px]">
        <button
          onClick={handleRemove}
          className="group flex h-[31px] w-full cursor-pointer flex-row items-center justify-center gap-2 rounded-lg bg-redish text-white hover:bg-gold hover:text-black"
        >
          <EraserIcon className="h-[20px] w-[20px]" />
          <p className="text-xs">{t('routes.remove')}</p>
        </button>
      </div>
    </div>
  );
};

export default StopCardAdded;
